import { useEffect } from 'react'
import type L from 'leaflet'
import type { PinnedElement } from '../types'

interface UsePinnedLocationsProps {
  mapRef: React.MutableRefObject<L.Map | null>
  poiOverlayRef: React.MutableRefObject<HTMLDivElement | null>
  isInitializedRef: React.MutableRefObject<boolean>
  pinnedLocations: Array<{ id: string; lat: number; lon: number; timestamp: number }>
  updateOverlayPositions: () => void
  pinnedElementsRef: React.MutableRefObject<Map<string, PinnedElement>>
}

export const usePinnedLocations = ({
  mapRef,
  poiOverlayRef,
  isInitializedRef,
  pinnedLocations,
  updateOverlayPositions,
  pinnedElementsRef,
}: UsePinnedLocationsProps) => {

  useEffect(() => {
    if (!poiOverlayRef.current || !mapRef.current || !isInitializedRef.current) return

    try {
      const container = mapRef.current.getContainer()
      if (!container || !container.offsetParent) {
        return
      }
    } catch (error) {
      return
    }

    // Remove pinned elements that are no longer in the list
    const currentIds = new Set(pinnedLocations.map(pin => pin.id))
    pinnedElementsRef.current.forEach((pinData, id) => {
      if (!currentIds.has(id)) {
        pinData.element.remove()
        pinnedElementsRef.current.delete(id)
      }
    })

    // Add or update pinned location elements
    pinnedLocations.forEach((pin) => {
      const existingPin = pinnedElementsRef.current.get(pin.id)

      if (!existingPin) {
        const pinElement = document.createElement('div')
        pinElement.className = 'pinned-location-overlay'
        
        // Calculate initial position immediately
        if (mapRef.current) {
          const initialPoint = mapRef.current.latLngToContainerPoint([pin.lat, pin.lon])
        
          pinElement.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 24px;
            height: 24px;
            margin-left: -12px;
            margin-top: -12px;
            border-radius: 50%;
            background-color: #ffd700;
            border: 3px solid #0b0d10;
            box-shadow: 0 2px 6px rgba(0,0,0,0.4);
            cursor: pointer;
            z-index: 980;
            transition: box-shadow 0.2s;
            transform: translate(${initialPoint.x}px, ${initialPoint.y}px);
            will-change: transform;
          `
        }

        pinElement.style.pointerEvents = 'auto'

        pinElement.addEventListener('mouseenter', () => {
          if (!mapRef.current) return
          try {
            const point = mapRef.current.latLngToContainerPoint([pin.lat, pin.lon])
            pinElement.style.transform = `translate(${point.x}px, ${point.y}px) scale(1.2)`
            pinElement.style.boxShadow = '0 3px 8px rgba(0,0,0,0.5)'
          } catch (error) {
            // Ignore
          }
        })
        pinElement.addEventListener('mouseleave', () => {
          if (!mapRef.current) return
          try {
            const point = mapRef.current.latLngToContainerPoint([pin.lat, pin.lon])
            pinElement.style.transform = `translate(${point.x}px, ${point.y}px) scale(1)`
            pinElement.style.boxShadow = '0 2px 6px rgba(0,0,0,0.4)'
          } catch (error) {
            // Ignore
          }
        })

        pinElement.title = 'Pinned Location'

        poiOverlayRef.current?.appendChild(pinElement)
        pinnedElementsRef.current.set(pin.id, {
          element: pinElement,
          lat: pin.lat,
          lon: pin.lon,
        })
      } else {
        if (Math.abs(existingPin.lat - pin.lat) > 0.0001 ||
          Math.abs(existingPin.lon - pin.lon) > 0.0001) {
          existingPin.lat = pin.lat
          existingPin.lon = pin.lon
        }
      }
    })

    updateOverlayPositions()
  }, [pinnedLocations, updateOverlayPositions, mapRef, poiOverlayRef, isInitializedRef, pinnedElementsRef])
}
